import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@mui/material';
import clsx from 'clsx';

/**
 * ONE CONTINUOUS ROW. There is deliberately no `position` here: the bar used
 * to split into a left group (the context tabs) and a right group floated
 * away with `ml-auto` (Drafts, Strategy, Time, Billing Rates), with a divider
 * between them. Every tab that lived on the right has since been removed or
 * folded into Economics, so the split left one lonely tab pushed to the far
 * edge and a hairline divider separating it from nothing.
 */
export interface TabItem {
  label: string;
  renderedLabel?: React.ReactNode;
  value: string;
  icon?: React.ReactNode;
  tooltip?: string;
  disabled?: boolean;
}

interface DetailTabBarProps {
  tabs: TabItem[];
  activeTab: string;
  onTabClick: (tabValue: string) => void;
  className?: string;
  showLabels?: boolean;
  /**
   * Anchor for a feature tour step, if the page runs one.
   *
   * A prop rather than a constant because the bar is shared now: a tour that
   * highlights "the matter tabs" must not also match a review's.
   */
  tourId?: string;
  /**
   * Pinned to the right of the bar, OUTSIDE the scroll container.
   *
   * That placement is the point: the tabs scroll horizontally when they
   * overflow, and anything sharing this row must not scroll away with them.
   * A sibling of the scroller stays put; a child would disappear off the
   * right edge exactly when the row got tight enough to need it.
   */
  actions?: React.ReactNode;
  /**
   * Pinned to the LEFT of the bar, before the tab scroller — the mirror of
   * `actions`, and for the same reason: it must not scroll away with the tabs.
   *
   * What goes here is what the page IS — Drafting puts its document line (the
   * title, its type, its matter, its age) in this slot, so the header is one
   * row instead of a title block stacked above a tab strip. It is
   * `min-w-0 flex-shrink`, so the title truncates before the tabs give up any
   * width; the tabs are the controls and must stay reachable.
   */
  leading?: React.ReactNode;
}

type IconComponent = React.ComponentType<{ className?: string }>;

const renderIcon = (icon: React.ReactNode, className?: string): React.ReactNode => {
  if (React.isValidElement(icon)) {
    return React.cloneElement(icon as React.ReactElement<any>, { className });
  }
  if (typeof icon === 'function') {
    const IconComp = icon as IconComponent;
    return <IconComp className={className} />;
  }
  return icon;
};

const DetailTabBar: React.FC<DetailTabBarProps> = ({
  tabs,
  activeTab,
  onTabClick,
  className,
  showLabels = true,
  tourId,
  actions,
  leading,
}) => {
  const { t } = useTranslation();
  const tabsRef = useRef<HTMLDivElement | null>(null);

  /**
   * Edge fades, so an overflowing bar LOOKS scrollable.
   *
   * The strip is its own full-width row now, and on a narrow window the last
   * tabs sit past the right edge. It has always been scrollable — but the
   * scrollbar is hidden, so there was nothing on screen to say so and a tab
   * could look simply absent. The fades are the affordance; scrolling itself
   * is the browser's.
   */
  const [edges, setEdges] = useState({ left: false, right: false });

  const measure = useCallback(() => {
    const el = tabsRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setEdges({ left: el.scrollLeft > 1, right: max > 1 && el.scrollLeft < max - 1 });
  }, []);

  useEffect(() => {
    measure();
    const el = tabsRef.current;
    if (!el) return;
    el.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure);
    // The tab set itself changes (economics on/off), not just the viewport.
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    observer?.observe(el);
    return () => {
      el.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
      observer?.disconnect();
    };
  }, [measure, tabs.length]);

  /** Keep the selected tab on screen when it is scrolled out of view. */
  useEffect(() => {
    const el = tabsRef.current;
    const active = el?.querySelector<HTMLElement>('[aria-pressed="true"]');
    active?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [activeTab]);

  const renderTab = (tab: TabItem) => {
    const isActive = activeTab === tab.value;
    const tabClasses = clsx(
      'nav-pill flex items-center px-3 py-1 text-xs font-medium rounded',
      isActive
        ? 'nav-pill-active text-primary-600 dark:text-primary-300'
        : 'text-fg-body',
      tab.disabled && 'opacity-50 cursor-not-allowed'
    );

    const iconClassName = clsx(
      'h-4 w-4 flex-shrink-0 transition-colors duration-200',
      isActive ? 'text-primary-600 dark:text-primary-300' : 'text-fg-muted'
    );

    return (
      <Tooltip key={tab.value} title={tab.tooltip || t(tab.label)} placement="top" arrow>
        <button
          onClick={() => {
            !tab.disabled && onTabClick(tab.value);
          }}
          disabled={tab.disabled}
          className={tabClasses}
          aria-label={t(tab.label)}
          aria-pressed={isActive}
        >
          <span className="flex items-center">
            {tab.icon && (
              <span className="mr-2 flex-shrink-0">
                {renderIcon(tab.icon, iconClassName)}
              </span>
            )}
            {showLabels && (
              <span className="whitespace-nowrap transition-colors duration-200">
                {tab.renderedLabel ?? t(tab.label)}
              </span>
            )}
          </span>
        </button>
      </Tooltip>
    );
  };

  return (
    <div className={clsx('flex items-center glass-bar border-b border-hairline-soft', className)}>
      {leading && (
        <div className="min-w-0 flex-1 flex items-center pl-3 pr-2 py-2">
          {leading}
        </div>
      )}
      {/* With a leading slot the tabs take their natural width and give it up
          last; without one they own the row, as every other page's bar does. */}
      <div className={clsx('relative min-w-0', leading ? 'flex-shrink' : 'flex-1')}>
      <div
        ref={tabsRef}
        className="flex overflow-x-auto scrollbar-hide px-2 py-2"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        <style>{`
          .scrollbar-hide::-webkit-scrollbar {
            display: none;
          }
          .scrollbar-hide {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
        `}</style>
        <div data-tour-id={tourId} className="flex space-x-1 min-w-max flex-shrink-0">
          {tabs.map((tab) => renderTab(tab))}
        </div>
      </div>

      {/* Non-interactive: they mark that there is more, they do not scroll. */}
      <div
        aria-hidden
        data-testid="tabbar-fade-left"
        className={clsx(
          'pointer-events-none absolute inset-y-0 left-0 w-8 transition-opacity duration-200',
          'bg-gradient-to-r from-bg to-transparent',
          edges.left ? 'opacity-100' : 'opacity-0'
        )}
      />
      <div
        aria-hidden
        data-testid="tabbar-fade-right"
        className={clsx(
          'pointer-events-none absolute inset-y-0 right-0 w-8 transition-opacity duration-200',
          'bg-gradient-to-l from-bg to-transparent',
          edges.right ? 'opacity-100' : 'opacity-0'
        )}
      />
      </div>

      {actions && (
        <div className="flex-shrink-0 flex items-center gap-2 pl-2 pr-3 py-2">
          {actions}
        </div>
      )}
    </div>
  );
};

export default DetailTabBar;
